import { BASE_URL } from './config'

// ─── Types ────────────────────────────────────────────────────────────────

export type SectionType = 'article' | 'faq' | 'video' | 'guide'

export interface FaqItem {
  q: string
  a: string
}

export interface GuideStep {
  heading: string
  body: string
  image: string | null
}

export interface ContentSection {
  id: string
  title: string
  slug: string
  parentId: string | null
  content: string | null
  mediaUrl: string | null
  videoUrl: string | null
  videoDurationSeconds: number | null
  sectionType: SectionType
  orderIndex: number
  isPublished: boolean
  data: { faqs?: FaqItem[]; steps?: GuideStep[] } | null
  createdAt: string
  updatedAt: string
  // Legacy / convenience fields some older payloads may include
  emoji?: string
  description?: string
  subtitle?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────

export function extractYouTubeId(url: string): string | null {
  if (!url) return null
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/,
  )
  return m ? m[1] : null
}

export function extractVimeoId(url: string): string | null {
  if (!url) return null
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  return m ? m[1] : null
}

export function getVideoEmbed(
  url: string,
): { provider: 'youtube' | 'vimeo' | 'external'; embedUrl: string } | null {
  if (!url) return null
  const yt = extractYouTubeId(url)
  if (yt)
    return {
      provider: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${yt}`,
    }
  const v = extractVimeoId(url)
  if (v)
    return {
      provider: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${v}`,
    }
  return { provider: 'external', embedUrl: url }
}

export function formatVideoDuration(seconds?: number | null): string | null {
  if (!seconds || seconds <= 0) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

// Normalize an API payload to an array of ContentSection.
// Public endpoint may return either an array directly or { content: [...] }.
function normalizeSections(raw: any): ContentSection[] {
  const arr: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.content)
      ? raw.content
      : Array.isArray(raw?.data)
        ? raw.data
        : []
  return arr
    .filter((s) => s && s.isPublished !== false)
    .map((s) => ({
      id: s.id,
      title: s.title,
      slug: s.slug,
      parentId: s.parentId ?? null,
      content: s.content ?? null,
      mediaUrl: s.mediaUrl ?? null,
      videoUrl: s.videoUrl ?? null,
      videoDurationSeconds: s.videoDurationSeconds ?? null,
      sectionType: (s.sectionType as SectionType) || 'article',
      orderIndex: typeof s.orderIndex === 'number' ? s.orderIndex : 0,
      isPublished: s.isPublished !== false,
      data: s.data ?? null,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      emoji: s.emoji,
      description: s.description,
      subtitle: s.subtitle,
    }))
    .sort((a, b) => a.orderIndex - b.orderIndex)
}

// ─── Cache ────────────────────────────────────────────────────────────────

const CACHE_KEY = 'aurora_content_cache_v1'

export function readContentCache(): ContentSection[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed as ContentSection[]
  } catch {
    return null
  }
}

function writeContentCache(sections: ContentSection[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(sections))
  } catch {
    // ignore quota errors
  }
}

// ─── API ──────────────────────────────────────────────────────────────────

export async function getContentSections(): Promise<ContentSection[]> {
  const res = await fetch(`${BASE_URL}/api/v1/content`)
  if (!res.ok) throw new Error(`Failed to fetch content: ${res.status}`)
  const json = await res.json()
  const sections = normalizeSections(json)
  writeContentCache(sections)
  return sections
}

export async function getContentSection(
  slug: string,
): Promise<ContentSection | null> {
  const res = await fetch(`${BASE_URL}/api/v1/content/${slug}`)
  if (!res.ok) {
    if (res.status === 404) return null
    throw new Error(`Failed to fetch section: ${res.status}`)
  }
  const json = await res.json()
  const payload = json?.content ?? json
  if (!payload || payload.isPublished === false) return null
  return normalizeSections([payload])[0] ?? null
}
